import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import NotificationModal from "components/NotificationModal";
import { AuthContext } from "context/appContext";
import useNotifications from "hooks/useNotifications";
import { FC, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ButtonCircle from "shared/Button/ButtonCircle";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Logo from "shared/Logo/Logo";
import MenuBar from "shared/MenuBar/MenuBar";
import Navigation from "shared/Navigation/Navigation";
import SwitchDarkMode from "shared/SwitchDarkMode/SwitchDarkMode";
import AvatarDropdown from "./AvatarDropdown";
import NotifyDropdown from "./NotifyDropdown";
import AdminNotificationSystem from "components/Admin/AdminNotificationSystem";
import { socket } from "utils/socket";
import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline";

export interface MainNav1Props {
  className?: string;
}

const MainNav1: FC<MainNav1Props> = ({ className = "" }) => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const {
    notifications,
    notification,
    loadMoreNotifications,
    deleteAllNotifications,
    loading,
    setNotification,
    isSmallScreen,
  } = useNotifications();

  const isHomePage = () => {
    return location.pathname === "/";
  };

  const [unreadCount, setUnreadCount] = useState<number>(0);
  let userId: any;

  if (
    authContext?.isAuthenticated &&
    (authContext?.user?.role === "client" ||
      authContext?.user?.role === "admin" ||
      authContext?.user?.role === "owner")
  ) {
    userId = authContext?.user?.id;
  }
  useEffect(() => {
    if (!userId) return;

    // Request initial unread count
    socket.emit("count_unread_messages", { userId });

    socket.on("connect", () => {
      socket.emit("count_unread_messages", { userId });
    });

    socket.on("unread_count", (data) => {
      console.log("Received unread_count:", data.unreadCount);
      setUnreadCount(data.unreadCount || 0);
    });

    return () => {
      socket.off("connect");
      socket.off("unread_count");
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    socket.on("new_message", () => {
      socket.emit("count_unread_messages", { userId });
    });

    return () => {
      socket.off("new_message");
    };
  }, [userId]);
  return (
    <>
      <div className={`relative z-10 nc-MainNav1 ${className}`}>
        <div
          className={
            isSmallScreen ? "" : "relative flex items-center justify-between px-4 py-4 lg:container lg:py-5"
          }
        >
          {isSmallScreen && !isHomePage() && (
            <div className="relative flex items-center justify-between px-4 py-4 lg:container lg:py-5">
              <ButtonCircle
                onClick={() => {
                  if (window.location.pathname.includes("/location/")) {
                    navigate("/espace-evenementiel");
                  } else if (window.location.pathname.includes("/prestations/")) {
                    navigate("/prestations");
                  } else {
                    window.history.back();
                  }
                }}
                className="absolute transform -translate-y-1/2 top-1/2"
                size="w-6 h-6 "
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </ButtonCircle>
            </div>
          )}

          <div className="items-center justify-start flex-1 hidden space-x-4 md:flex sm:space-x-10">
            <Logo className="w-40" />
          </div>

          <div className="flex items-center justify-center flex-grow">
            <Navigation />
          </div>

          <div className="items-center justify-end flex-1 flex-shrink-0 hidden md:flex lg:flex-none text-neutral-700 dark:text-neutral-100">
            {/* Desktop (xl) view */}
            <div className="hidden xl:flex items-center space-x-0.5">
              <div className="px-1" />

              {/* Render 'Ajouter votre espace' based on user role */}
              {authContext?.user?.role !== "admin" &&
                authContext?.user?.role !== "creator" &&
                authContext?.user?.role !== "service" && (
                  <div className="px-3">
                    <Link
                      to="/add/location"
                      className="items-center hidden px-4 py-2 text-sm font-medium text-gray-700 border rounded-full text-opacity-90 xl:inline-flex group border-neutral-300 hover:border-neutral-400 dark:border-neutral-700 dark:text-neutral-300 hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                    >
                      Ajouter votre espace
                    </Link>
                  </div>
                )}

              {/* Render 'Ajouter votre prestation' for 'service' role */}
              {authContext?.user?.role === "service" && (
                <div className="px-3">
                  <Link
                    to="/add/service"
                    className="items-center hidden px-4 py-2 text-sm font-medium text-gray-700 border rounded-full text-opacity-90 xl:inline-flex group border-neutral-300 hover:border-neutral-400 dark:border-neutral-700 dark:text-neutral-300 hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                  >
                    Ajouter votre prestation
                  </Link>
                </div>
              )}

              {/* Dark mode switch */}
              <SwitchDarkMode />

              {/* Content for authenticated users */}
              <div className="flex items-center justify-center space-x-2">
                {authContext.isAuthenticated && (
                  <>
                    {/* Notifications */}
                    <div className="">
                      {authContext?.user?.role === "admin" ? (
                        <AdminNotificationSystem />
                      ) : (
                        <NotifyDropdown
                          className="-ml-2 xl:-ml-1"
                          notifications={notifications}
                          loadMoreNotifications={loadMoreNotifications}
                          deleteAllNotifications={deleteAllNotifications}
                          loading={loading}
                        />
                      )}
                    </div>

                    {/* Messages link with unread notifications */}
                    {(authContext?.user?.role === "client" ||
                      authContext?.user?.role === "admin" ||
                      authContext?.user?.role === "owner") && (
                      <div className="flex items-center justify-center rounded-full w-11 h-11 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:outline-none">
                        <Link
                          to={
                            authContext?.user?.role === "client" || authContext?.user?.role === "owner"
                              ? "/client/messages"
                              : "/client/messages-support"
                          }
                        >
                          <div className="relative flex items-center justify-center">
                            <ChatBubbleBottomCenterTextIcon className="flex-shrink-0 w-5 h-5" />
                            {unreadCount > 0 && (
                              <span className="absolute flex items-center justify-center w-5 h-5 bg-blue-500 rounded-full top-1.5 right-1.5">
                                <span className="text-xs font-medium text-white">
                                  {unreadCount > 9 ? "9+" : unreadCount}
                                </span>
                              </span>
                            )}
                          </div>
                        </Link>
                      </div>
                    )}
                    {/* Avatar dropdown for authenticated users */}
                    <AvatarDropdown />
                  </>
                )}
              </div>

              {/* Login Button for unauthenticated users */}
              {!authContext.isAuthenticated && <ButtonPrimary href="/login">Compte</ButtonPrimary>}
            </div>

            {/* Mobile (xl) view */}
            <div className="flex items-center xl:hidden">
              <SwitchDarkMode />
              <div className="px-0.5" />
              <MenuBar />
            </div>
          </div>
        </div>
      </div>
      {notification && (
        <>
          <NotificationModal
            onHideModal={() => {
              setNotification(null);
            }}
            state={notification?.sender}
            message={notification?.message}
          ></NotificationModal>
        </>
      )}
    </>
  );
};

export default MainNav1;
